import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Flashbar,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Spinner,
  Textarea,
} from '@amzn/awsui-components-react-v3/polaris';
import DetailsPanel from '../../Common/DetailsPanel';
import ExcludedArcsField from '../../Common/ExcludedArcsField';
import { Messages, RetryAlert } from '../../Common/ErrorMessages';
import { useLocales } from '../../../utils/locales';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';
// eslint-disable-next-line no-unused-vars
import * as baseRates from '../BaseRates/baseRates';
import ChangeSetImpactPanel from '../ChangeSetImpact/ChangeSetImpactPanel';
import ChangeSetReview, { ReviewOutcomeFlash } from '../ChangeSetReview/ChangeSetReview';
// eslint-disable-next-line no-unused-vars
import { ReviewOutcome } from '../ChangeSetReview/changeSetReview';
import { findLatestPendingChangeSetId } from '../ChangeSetImpact/changeSetImpact';
import PricingModelChangeSets from './PricingModelChangeSets';
import PricingModelPreview from './PricingModelPreview';
import { PRICING_MODELS_ROUTE } from './PricingModelsList';
import * as pricingModels from './pricingModels';

interface PricingModelEditorProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPost: (path: string, body: object) => Promise<unknown>;
    httpPut: (path: string, body: object) => Promise<unknown>;
  };
  navigate: (path: string, state: pricingModels.SubmittedState) => void;
  // Absent when creating a new pricing model.
  priceSchemeId?: string;
  // The change set that created the model, when arriving from its create.
  submittedChangeSetId?: string;
}

const modelPath = (priceSchemeId: string): string =>
  `/pricingModels/${encodeURIComponent(priceSchemeId)}`;

const LoadError = ({ id, messages }: { id: string; messages: string[] }): JSX.Element => (
  <Alert id={id} type="error">
    <Messages messages={messages} />
  </Alert>
);

const PricingModelEditor = ({
  apiClient,
  navigate,
  priceSchemeId,
  submittedChangeSetId,
}: PricingModelEditorProps): JSX.Element => {
  const editing = priceSchemeId !== undefined;
  // An existing model opens read-only; Edit shows the form.
  const [editMode, setEditMode] = useState(false);
  const [values, setValues] = useState<pricingModels.FormValues>(pricingModels.emptyForm);
  // The form as last loaded or saved, which the view shows and Edit starts from.
  const [savedValues, setSavedValues] = useState<pricingModels.FormValues>(pricingModels.emptyForm);
  const [fieldErrors, setFieldErrors] = useState<pricingModels.FieldErrors>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | undefined>(submittedChangeSetId);
  const [preview, setPreview] = useState<pricingModels.GeneratedRate[] | null>(null);
  const [reviewOutcome, setReviewOutcome] = useState<ReviewOutcome | null>(null);

  const [model, setModel] = useState<pricingModels.PricingModel | null>(null);
  const [modelLoading, setModelLoading] = useState(editing);
  const [modelErrors, setModelErrors] = useState<string[]>([]);
  const [baseRate, setBaseRate] = useState<baseRates.BaseRates | null>(null);
  const [baseRateMissing, setBaseRateMissing] = useState(false);
  const [baseRateErrors, setBaseRateErrors] = useState<string[]>([]);

  const loadModel = useCallback((): void => {
    if (priceSchemeId === undefined) {
      return;
    }
    setModelLoading(true);
    setModelErrors([]);
    apiClient
      .httpGet(modelPath(priceSchemeId))
      .then(response => {
        const loaded = response as pricingModels.PricingModel;
        setModel(loaded);
        const form = pricingModels.formFromModel(loaded);
        setValues(form);
        setSavedValues(form);
        const changeSet = pricingModels.findChangeSetToEdit(loaded);
        const draft = changeSet && changeSet.draft;
        setPreview(draft ? draft.basePrices || [] : null);
      })
      .catch((error: ApiError) => setModelErrors(pricingModels.describeError(error)))
      .finally(() => setModelLoading(false));
  }, [apiClient, priceSchemeId]);

  const loadBaseRate = useCallback((): void => {
    apiClient
      .httpGet('/baseRate')
      .then(response => setBaseRate(response as baseRates.BaseRates))
      .catch((error: ApiError) => {
        if (error.status === 404) {
          setBaseRateMissing(true);
        } else {
          setBaseRateErrors(pricingModels.describeError(error));
        }
      });
  }, [apiClient]);

  useEffect(loadModel, [loadModel]);
  useEffect(loadBaseRate, [loadBaseRate]);
  const { locales, errors: localeErrors } = useLocales(apiClient, pricingModels.describeError);

  const options = useMemo(
    () =>
      pricingModels.arcOptions(baseRate ? baseRate.basePrices : [], values.excludedArcs, locales),
    [baseRate, values.excludedArcs, locales]
  );

  const change = (field: pricingModels.FormField, value: string | string[]): void => {
    setValues({ ...values, [field]: value });
    setFieldErrors({ ...fieldErrors, [field]: undefined });
  };

  const startEditing = (): void => {
    setValues(savedValues);
    setFieldErrors({});
    setFormErrors([]);
    setSubmitted(undefined);
    setEditMode(true);
  };

  const cancelEditing = (): void => {
    setValues(savedValues);
    setFieldErrors({});
    setFormErrors([]);
    setEditMode(false);
  };

  const succeeded = (response: unknown): void => {
    const changeSet = response as pricingModels.ChangeSet;
    if (!editing) {
      navigate(`${PRICING_MODELS_ROUTE}/${encodeURIComponent(changeSet.priceSchemeId)}`, {
        submittedChangeSetId: changeSet.changeSetId,
      });
      return;
    }
    setSubmitted(changeSet.changeSetId);
    setSavedValues(values);
    setEditMode(false);
    setPreview(changeSet.draft.basePrices || []);
    if (model) {
      setModel({
        ...model,
        latestChangeSetId: changeSet.changeSetId,
        latestChangeSetAt: changeSet.createdAt,
        latestChangeSetAuthor: changeSet.author,
        pendingChangeSetCount: model.pendingChangeSetCount + 1,
        changeSets: [pricingModels.summaryOf(changeSet), ...model.changeSets],
      });
    }
  };

  const reviewed = (outcome: ReviewOutcome): void => {
    setReviewOutcome(outcome);
    setSubmitted(undefined);
    loadModel();
  };

  const submit = (): void => {
    setSubmitted(undefined);
    setFormErrors([]);
    const validationErrors = pricingModels.validateForm(values, !editing);
    setFieldErrors(validationErrors);
    if (pricingModels.hasFieldErrors(validationErrors)) {
      return;
    }
    const request =
      priceSchemeId === undefined
        ? pricingModels.toCreateRequest(values)
        : pricingModels.toUpdateRequest(values);
    setSubmitting(true);
    const saving =
      priceSchemeId === undefined
        ? apiClient.httpPost('/pricingModels', request)
        : apiClient.httpPut(modelPath(priceSchemeId), request);
    // Submitting ends before a create navigates away, so no state is set after unmount.
    saving
      .then(response => {
        setSubmitting(false);
        succeeded(response);
      })
      .catch((error: ApiError) => {
        setSubmitting(false);
        const failure = pricingModels.describeSaveError(error, request, locales);
        setFieldErrors(failure.fieldErrors);
        setFormErrors(failure.formErrors);
      });
  };

  if (modelLoading) {
    return (
      <Box id="pricingModelLoading" textAlign="center">
        <Spinner /> Loading pricing model
      </Box>
    );
  }
  if (editing && !model) {
    return <LoadError id="pricingModelLoadError" messages={modelErrors} />;
  }

  const pendingChangeSetId = model ? findLatestPendingChangeSetId(model.changeSets) : null;
  const formShown = !editing || editMode;
  // After a failed reload the form may hold a version a review replaced; saving it would revert it.
  const reloadFailed = modelErrors.length > 0;

  const multiplierField = ({
    field,
    label,
  }: {
    field: pricingModels.Multiplier;
    label: string;
  }): JSX.Element => (
    <FormField
      key={field}
      id={`${field}-field`}
      label={label}
      constraintText="Greater than 0 and less than 2, up to 3 decimal places."
      errorText={fieldErrors[field]}
    >
      <Input
        id={field}
        type="number"
        inputMode="decimal"
        step={0.001}
        value={values[field]}
        invalid={Boolean(fieldErrors[field])}
        disabled={submitting}
        onChange={({ detail }): void => change(field, detail.value)}
      />
    </FormField>
  );

  return (
    <SpaceBetween size="m">
      <ReviewOutcomeFlash outcome={reviewOutcome} onDismiss={(): void => setReviewOutcome(null)} />
      {submitted && (
        <Flashbar
          id="pricingModelSubmitted"
          items={[
            {
              type: 'success',
              content: `Submitted change set ${submitted} (pending approval)`,
            },
          ]}
        />
      )}
      {formShown && baseRateMissing && (
        <Alert id="pricingModelBaseRateMissing" type="warning">
          {`Base rates are required before ${editing ? 'saving' : 'creating'} a pricing model.`}{' '}
          <Link to="/settings/baseRate">Set up base rates</Link>
        </Alert>
      )}
      {baseRateErrors.length > 0 && (
        <LoadError id="pricingModelBaseRateError" messages={baseRateErrors} />
      )}
      {localeErrors.length > 0 && (
        <LoadError id="pricingModelLocalesError" messages={localeErrors} />
      )}
      {formErrors.length > 0 && <LoadError id="pricingModelFormErrors" messages={formErrors} />}
      {reloadFailed && (
        <RetryAlert
          id="pricingModelLoadError"
          header="Could not reload the pricing model"
          messages={modelErrors}
          retryId="retryPricingModel"
          onRetry={loadModel}
        />
      )}
      {model && (
        <Header
          variant="h2"
          description={`Pending change sets: ${model.pendingChangeSetCount}`}
          actions={
            formShown ? (
              undefined
            ) : (
              <Button id="editPricingModel" onClick={startEditing}>
                Edit
              </Button>
            )
          }
        >
          {model.name}
        </Header>
      )}
      {model && (
        <DetailsPanel
          items={[
            { label: 'Name', value: model.name },
            { label: 'Description', value: valueOrDash(model.description) },
            ...pricingModels.MULTIPLIERS.map(({ field, label }) => ({
              label,
              value: <span id={`${field}Value`}>{savedValues[field]}</span>,
            })),
            {
              label: 'Excluded arcs',
              value: (
                <span id="excludedArcsValue">
                  {valueOrDash(
                    savedValues.excludedArcs
                      .map(arc => pricingModels.arcLabel(pricingModels.parseArc(arc), locales))
                      .join(', ')
                  )}
                </span>
              ),
            },
            {
              label: 'Created',
              value: `${convertDateTimePretty(model.createdAt)} by ${model.author}`,
            },
            {
              label: 'Latest change',
              value: pricingModels.latestChangeText(
                model.latestChangeSetAt,
                model.latestChangeSetAuthor
              ),
            },
          ]}
        />
      )}
      {formShown && (
        <Container
          id="pricingModelForm"
          header={
            <Header variant="h2">{editing ? 'Edit pricing model' : 'Create pricing model'}</Header>
          }
        >
          <SpaceBetween size="m">
            <FormField
              id="name-field"
              label="Name"
              description={editing ? 'The name cannot be changed.' : undefined}
              errorText={fieldErrors.name}
            >
              <Input
                id="name"
                value={values.name}
                invalid={Boolean(fieldErrors.name)}
                disabled={editing || submitting}
                onChange={({ detail }): void => change('name', detail.value)}
              />
            </FormField>
            <FormField
              id="description-field"
              label="Description"
              description={editing ? 'The description cannot be changed.' : undefined}
              constraintText={
                editing ? undefined : 'Required. The description cannot be changed later.'
              }
              errorText={fieldErrors.description}
            >
              <Textarea
                id="description"
                value={values.description}
                invalid={Boolean(fieldErrors.description)}
                disabled={editing || submitting}
                onChange={({ detail }): void => change('description', detail.value)}
              />
            </FormField>
            {pricingModels.MULTIPLIERS.map(multiplierField)}
            <ExcludedArcsField
              options={options}
              selected={values.excludedArcs}
              description="Language pairs from the base rates that this pricing model does not price."
              errorText={fieldErrors.excludedArcs}
              disabled={submitting}
              onChange={(selected): void => change('excludedArcs', selected)}
            />
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                id="submitPricingModel"
                variant="primary"
                loading={submitting}
                disabled={submitting || baseRateMissing || reloadFailed}
                onClick={submit}
              >
                {editing ? 'Save' : 'Create'}
              </Button>
              {editing && (
                <Button id="cancelPricingModel" disabled={submitting} onClick={cancelEditing}>
                  Cancel
                </Button>
              )}
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      )}
      {preview && <PricingModelPreview rates={preview} />}
      {model && <PricingModelChangeSets changeSets={model.changeSets} />}
      {pendingChangeSetId && (
        <ChangeSetImpactPanel
          apiClient={apiClient}
          changeSetId={pendingChangeSetId}
          locales={locales}
          actions={
            !editMode && !submitting ? (
              <ChangeSetReview
                key={pendingChangeSetId}
                apiClient={apiClient}
                changeSetId={pendingChangeSetId}
                onReviewed={reviewed}
              />
            ) : (
              undefined
            )
          }
        />
      )}
    </SpaceBetween>
  );
};

PricingModelEditor.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  priceSchemeId: PropTypes.string,
  submittedChangeSetId: PropTypes.string,
};

export default PricingModelEditor;
